import BaseElement from './base_element';

export default class TextElement extends BaseElement {
  constructor(managers, text, x, y, properties = {}) {
    super(managers, properties);
    this.element = this._createDomElement(text, x, y, properties);
    this._textBeforeEdit = this.getText();
    this._setupEvents();
  }

  _createDomElement(text, x, y, properties) {
    const element = document.createElement('div');
    element.className = 'canvas-element text-element';
    element.contentEditable = false;
    element.style.left = x + 'px';
    element.style.top = y + 'px';
    element.style.fontSize = properties.fontSize || '24px';
    element.style.fontFamily = properties.fontFamily || 'Open Sans';
    element.style.color = properties.color || '#000000';
    element.style.textAlign = properties.textAlign || 'center';
    element.dataset.type = 'text';
    element.dataset.id = this.id;

    if (properties.fontWeight) element.style.fontWeight = properties.fontWeight;
    if (properties.fontStyle) element.style.fontStyle = properties.fontStyle;
    if (properties.width) element.style.width = properties.width;

    const textWrapper = document.createElement('span');
    textWrapper.className = 'text-content';
    textWrapper.innerText = text;
    if (properties.textDecoration) textWrapper.style.textDecoration = properties.textDecoration;
    element.appendChild(textWrapper);

    const resizeHandle = document.createElement('div');
    resizeHandle.className = 'resize-handle';
    element.appendChild(resizeHandle);

    return element;
  }

  _setupEvents() {
    this.element.addEventListener('dblclick', (e) => {
      if (e.target.classList.contains('resize-handle')) return;
      this._enterEditMode();
    });

    this.element.addEventListener('blur', (e) => {
      if (e.target.classList.contains('text-content')) {
        this.exitEditMode();
      }
    }, true);

    this.element.addEventListener('mousedown', (e) => {
      if (e.target.classList.contains('resize-handle')) {
        this.managers.resize.startResize(e, this.element);
      } else if (!this._isEditing()) {
        this.managers.elementActions.handleSelectionAndDrag(e, this.element);
      }
    });

    this.element.addEventListener('paste', (e) => {
      if (!this._isEditing()) return;

      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
    });
  }

  _isEditing() {
    const textWrapper = this.element.querySelector('.text-content');
    return textWrapper && textWrapper.contentEditable === 'true';
  }

  isEditing() {
    return this._isEditing();
  }

  _enterEditMode() {
    const textWrapper = this.element.querySelector('.text-content');
    if (textWrapper) {
      textWrapper.contentEditable = true;
      textWrapper.focus();
      document.execCommand('selectAll', false, null);
    }

    const resizeHandle = this.element.querySelector('.resize-handle');
    if (resizeHandle) {
      resizeHandle.style.display = 'none';
    }
  }

  exitEditMode() {
    const textWrapper = this.element.querySelector('.text-content');
    if (textWrapper) {
      textWrapper.contentEditable = false;
    }

    const resizeHandle = this.element.querySelector('.resize-handle');
    if (resizeHandle) {
      resizeHandle.style.display = '';
    }

    const currentText = this.getText();
    if (this.managers.history && currentText !== this._textBeforeEdit) {
      this.managers.history.saveState();
      this._textBeforeEdit = currentText;
    }
  }

  setFontSize(size) {
    this.element.style.fontSize = size + 'px';
    this._saveHistoryState();
  }

  setFontFamily(family) {
    this.element.style.fontFamily = family;
    this._saveHistoryState();
  }

  setColor(color) {
    this.element.style.color = color;
    this._saveHistoryState();
  }

  setBold(isBold) {
    this.element.style.fontWeight = isBold ? 'bold' : 'normal';
    this._saveHistoryState();
  }

  setItalic(isItalic) {
    this.element.style.fontStyle = isItalic ? 'italic' : 'normal';
    this._saveHistoryState();
  }

  setUnderline(isUnderline) {
    const textWrapper = this.element.querySelector('.text-content');

    if (textWrapper) {
      textWrapper.style.textDecoration = isUnderline ? 'underline' : 'none';
    }

    this._saveHistoryState();
  }

  setWidth(width) {
    this.element.style.width = width;
    this._saveHistoryState();
  }

  setTextAlign(alignment) {
    this.element.style.textAlign = alignment;
    this._saveHistoryState();
  }

  _saveHistoryState() {
    if (this.managers.history) {
      this.managers.history.saveState();
    }
  }

  getText() {
    const textWrapper = this.element.querySelector('.text-content');
    return textWrapper ? textWrapper.innerText : '';
  }

  getFontSize() {
    return parseInt(this.element.style.fontSize);
  }

  getFontFamily() {
    return this.element.style.fontFamily.replace(/['"]/g, '');
  }

  getColor() {
    return this.element.style.color;
  }

  isBold() {
    return this.element.style.fontWeight === 'bold';
  }

  isItalic() {
    return this.element.style.fontStyle === 'italic';
  }

  isUnderline() {
    const textWrapper = this.element.querySelector('.text-content');
    return textWrapper?.style.textDecoration?.includes('underline') || false;
  }

  getTextAlign() {
    return this.element.style.textAlign;
  }

  serialize() {
    const textWrapper = this.element.querySelector('.text-content');
    return {
      ...this.getSerializeBase(),
      type: 'text',
      text: this.getText(),
      fontSize: this.element.style.fontSize,
      color: this.element.style.color,
      fontWeight: this.element.style.fontWeight,
      fontStyle: this.element.style.fontStyle,
      textDecoration: textWrapper?.style.textDecoration,
      width: this.element.style.width,
      textAlign: this.element.style.textAlign,
      fontFamily: this.getFontFamily(),
      offsetWidth: this.element.offsetWidth,  // Actual rendered width
      lineHeight: this._getComputedLineHeight()  // Browser's computed line height
    };
  }

  _getComputedLineHeight() {
    const textContent = this.element.querySelector('.text-content');
    const computed = window.getComputedStyle(textContent || this.element);
    const lineHeight = computed.lineHeight;
    const fontSize = parseInt(this.element.style.fontSize);

    // Handle various CSS line-height values
    if (lineHeight === 'normal' || lineHeight === 'initial' || lineHeight === 'unset') {
      // Use browser default of 1.2x for normal line height
      return fontSize * 1.2;
    }

    // If lineHeight ends with 'px', parse as pixels
    if (lineHeight.endsWith('px')) {
      return parseFloat(lineHeight);
    }

    // If lineHeight ends with 'em' or is a unitless number (ratio)
    if (lineHeight.endsWith('em') || !isNaN(parseFloat(lineHeight))) {
      const ratio = parseFloat(lineHeight);
      return fontSize * ratio;
    }

    // If lineHeight is a percentage
    if (lineHeight.endsWith('%')) {
      const percentage = parseFloat(lineHeight);
      return fontSize * (percentage / 100);
    }

    // Fallback to 1.2x if we can't parse it
    return fontSize * 1.2;
  }

  static fromData(data, managers) {
    return new TextElement(
      managers,
      data.text,
      parseInt(data.left),
      parseInt(data.top),
      {
        id: data.id,
        fontSize: data.fontSize,
        fontFamily: data.fontFamily,
        color: data.color,
        fontWeight: data.fontWeight,
        fontStyle: data.fontStyle,
        textDecoration: data.textDecoration,
        width: data.width,
        textAlign: data.textAlign
      }
    );
  }
}
